import { Buffer } from "buffer";
import clsx from "clsx";
import { motion } from "motion/react";
import {
  createContext,
  FC,
  PropsWithChildren,
  Suspense,
  useContext,
  useEffect,
  useRef,
  useState,
  useTransition,
} from "react";
import {
  PreloadedQuery,
  useFragment,
  useLazyLoadQuery,
  usePreloadedQuery,
  useQueryLoader,
} from "react-relay";
import { useLocation, useNavigate } from "react-router-dom";
import { graphql } from "relay-runtime";
import {
  Button,
  Input,
  nFormatter,
  SortDir as TableSortDir,
  SortableTableHeader,
  TableColumns,
  TableFrame,
  TablePagination,
  TranslationProvider,
  useLocale,
  useTablePagination,
  useTranslation,
} from "rujira.ui";
import rujiraLogo from "rujira.ui/assets/images/rujira.png";

import { Address } from "rujira.js";
import { Icons } from "rujira.ui";
import { getStaticProductSeo, ProductSeoHelmet } from "../seo";
import { useWalletAccounts } from "../services/accounts";
import { useCapture } from "../services/useCapture";
import { LeaderboardAccountQuery } from "./__generated__/LeaderboardAccountQuery.graphql";
import { LeaderboardItemFragment$key } from "./__generated__/LeaderboardItemFragment.graphql";
import {
  LeaderboardLeaderboardQuery,
  LeagueLeaderboardSortBy,
  SortDir,
} from "./__generated__/LeaderboardLeaderboardQuery.graphql";
import { LeaderboardQuery } from "./__generated__/LeaderboardQuery.graphql";
import bg from "./assets/background.jpg";
import bolt from "./assets/bolt.webp";
import bronze from "./assets/bronze.png";
import silver from "./assets/silver.png";
import trophy from "./assets/trophy.png";

const LEADERBOARD_COLUMNS = [
  { id: "rank", width: "10%", minWidth: "5rem" },
  { id: "change", width: "20%", minWidth: "8rem" },
  { id: "points", width: "30%", minWidth: "15rem" },
  { id: "transactions", width: "40%", minWidth: "14rem" },
] as const;

const Meta = () => {
  const { locale } = useLocale();

  return <ProductSeoHelmet seo={getStaticProductSeo("/leaderboard", locale)} />;
};

const query = graphql`
  query LeaderboardQuery {
    league {
      league
      season
      stats {
        totalPoints
        participants
      }
    }
  }
`;

const leaderboardQuery = graphql`
  query LeaderboardLeaderboardQuery(
    $search: String
    $sortBy: LeagueLeaderboardSortBy!
    $sortDir: SortDir!
  ) {
    league {
      leaderboard(
        first: 50
        search: $search
        sortBy: $sortBy
        sortDir: $sortDir
      ) {
        edges {
          node {
            address
            ...LeaderboardItemFragment
          }
        }
      }
    }
  }
`;

const accountQuery = graphql`
  query LeaderboardAccountQuery($id: ID!) {
    node(id: $id) {
      ... on LeagueAccount {
        address
        points
        rank
        rankPrevious
      }
    }
  }
`;

const context = createContext<
  [
    {
      sortBy: LeagueLeaderboardSortBy;
      sortDir: SortDir;
      search: string | undefined;
    },
    {
      setSortBy: (v: LeagueLeaderboardSortBy) => void;
      setSortDir: (v: SortDir) => void;
      setSearch: (v: string) => void;
    },
  ]
>([
  {
    sortBy: "RANK",
    sortDir: "ASC",
    search: "",
  },
  {
    setSortBy: () => {},
    setSortDir: () => {},
    setSearch: () => {},
  },
]);

const Context: FC<PropsWithChildren> = ({ children }) => {
  const [sortBy, setSortBy] = useState<LeagueLeaderboardSortBy>("RANK");
  const [sortDir, setSortDir] = useState<SortDir>("ASC");
  const [search, setSearch] = useState("");
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (!params.has("by") && !params.has("dir") && !params.has("q")) return;

    params.delete("by");
    params.delete("dir");
    params.delete("q");
    const nextSearch = params.toString();
    navigate(
      {
        pathname: location.pathname,
        search: nextSearch ? `?${nextSearch}` : "",
        hash: location.hash,
      },
      { replace: true }
    );
  }, [location.hash, location.pathname, location.search, navigate]);

  return (
    <context.Provider
      value={[
        {
          sortBy,
          sortDir,
          search: search || undefined,
        },
        {
          setSortBy,
          setSortDir,
          setSearch,
        },
      ]}>
      {children}
    </context.Provider>
  );
};

export const Leaderboard: FC = () => {
  return (
    <TranslationProvider namespace="leagues">
      <LeaderboardContent />
    </TranslationProvider>
  );
};

const LeaderboardContent: FC = () => {
  const { t } = useTranslation();
  const data = useLazyLoadQuery<LeaderboardQuery>(query, {});
  const league = data.league[0];
  // A league position is the wallet's own, public one: it can be shared.
  const { selected } = useWalletAccounts();

  return (
    <Context>
      <Meta />
      <motion.div
        className="leaderboard__background"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 4 }}
        style={{ backgroundImage: `url(${bg})` }}
      />
      <div className="rujira__main">
        <div className="rujira__inner--pad leaderboard">
          <div className="rujira__inner">
            <h1 className="h1">{t("leaderboard")}</h1>
            <p className="intro">{t("pageDescription")}</p>
            <div className="row wrap pad mt-6">
              <div className="col-12 col-sm-4 col-md-3">
                <div className="card card--semi-transparent card--blur h-full p-3">
                  <h2 className="fs-16 lh-22 fw-400 color-grey">
                    {t("participatingAddresses")}
                  </h2>
                  <h3 className="fs-22 fs-md-32 condensed fw-500 mt-1">
                    {league.stats.participants}
                  </h3>
                </div>
              </div>
              <div className="col-12 col-sm-4 col-md-3 mt-2 mt-sm-0">
                <div className="card card--semi-transparent card--blur h-full p-3">
                  <h2 className="fs-16 lh-22 fw-400 color-grey">
                    {t("pointsGenerated")}
                  </h2>
                  <h3 className="fs-22 fs-md-32 condensed fw-500 mt-1">
                    {nFormatter(BigInt(league.stats.totalPoints), 2)}
                  </h3>
                </div>
              </div>
              <div className="col-12 col-sm-4 col-md-6 mt-2 mt-sm-0">
                <Suspense>
                  {selected && (
                    <AccountProvider
                      Component={Position}
                      account={selected.address}
                    />
                  )}
                </Suspense>
              </div>
            </div>
            <div className="card card--semi-transparent card--blur mt-4 p-2">
              <Filters />
              <Suspense fallback={<LeaderboardFallback />}>
                <QueryProvider />
              </Suspense>
            </div>
          </div>
        </div>
      </div>
    </Context>
  );
};

const Filters: FC = () => {
  const { t } = useTranslation("leagues");
  const [{ search }, { setSearch }] = useContext(context);

  return (
    <div className="row wrap pad--sm">
      <div className="col-12">
        <Input
          value={search || ""}
          onChange={(e) => setSearch(e.currentTarget.value)}
          label={t("search")}>
          {search && (
            <Icons.MinusCircle
              className="input-container__clear"
              onClick={() => setSearch("")}
            />
          )}
        </Input>
      </div>
    </div>
  );
};

const QueryProvider: FC = () => {
  const [q, loadQuery] =
    useQueryLoader<LeaderboardLeaderboardQuery>(leaderboardQuery);
  const [variables] = useContext(context);
  const [, transition] = useTransition();
  useEffect(() => {
    transition(() => {
      loadQuery(variables);
    });
  }, [variables]);

  return q ? <Content q={q} /> : <LeaderboardFallback />;
};

const Content: FC<{
  q: PreloadedQuery<LeaderboardLeaderboardQuery>;
}> = ({ q }) => {
  const d = usePreloadedQuery(leaderboardQuery, q);

  const league = d.league[0];

  const leaderboard = league.leaderboard;
  const [{ sortBy, sortDir, search }] = useContext(context);
  const showPodium =
    search === undefined
      ? (sortBy === "POINTS" && sortDir === "DESC") ||
        (sortBy === "RANK" && sortDir === "ASC")
      : false;
  const entries =
    leaderboard?.edges?.flatMap((edge) => (edge?.node ? [edge.node] : [])) ??
    [];
  const { pageRows, pagination } = useTablePagination(entries, {
    resetKey: `${sortBy}:${sortDir}:${search ?? ""}`,
  });
  const showPagePodium =
    showPodium && pagination.pageIndex === 0 && pageRows.length >= 3;
  const regularRows = showPagePodium ? pageRows.slice(3) : pageRows;

  return (
    <>
      <TableFrame
        className="leaderboard__table mt-2"
        columns={LEADERBOARD_COLUMNS}
        tableClassName="table table--fixed condensed mb-0">
        <Header />
        <tbody>
          {showPagePodium ? (
            <Podium items={[pageRows[0], pageRows[1], pageRows[2]]} />
          ) : null}
          {regularRows.map((node) => (
            <Item key={node.address} k={node} />
          ))}
          <NoResults count={entries.length} />
        </tbody>
      </TableFrame>
      <TablePagination {...pagination} />
    </>
  );
};
const itemFragment = graphql`
  fragment LeaderboardItemFragment on LeagueLeaderboardEntry {
    address
    points
    rank
    rankPrevious
    totalTx
    badges
  }
`;

const Podium: FC<{
  items: [
    LeaderboardItemFragment$key,
    LeaderboardItemFragment$key,
    LeaderboardItemFragment$key,
  ];
}> = ({ items }) => {
  return (
    <tr className="table--no-hover">
      <td colSpan={LEADERBOARD_COLUMNS.length} className="nopad">
        <div className="gradient-card-container">
          <div className="gradient-card gradient-card--gold bg-deepBlack px-1 condensed fs-14 py-1">
            <table className="leaderboard__podium table table--fixed table--no-hover condensed mb-0">
              <TableColumns columns={LEADERBOARD_COLUMNS} />
              <tbody>
                <PodiumItem k={items[0]} />
                <PodiumItem k={items[1]} />
                <PodiumItem k={items[2]} />
              </tbody>
            </table>
          </div>
        </div>
      </td>
    </tr>
  );
};

const PodiumItem: FC<{
  k: LeaderboardItemFragment$key;
}> = ({ k }) => {
  const { t } = useTranslation("leagues");
  const item = useFragment(itemFragment, k);
  const rankChange = item.rankPrevious ? item.rankPrevious - item.rank : 0;

  return (
    <tr
      className={clsx({
        "fs-22 fw-500": true,
        gold: item.rank === 1,
        silver: item.rank === 2,
        bronze: item.rank === 3,
      })}>
      <td className="text-center">{item.rank}</td>
      <td
        className={clsx({
          "text-center": true,
          "color-teal": rankChange > 0,
          "color-red": rankChange < 0,
          "color-grey": rankChange === 0,
        })}>
        {rankChange > 0 && <Icons.TrendUp className="w-2.5" />}
        {rankChange < 0 && <Icons.TrendDown className="w-2.5" />}{" "}
        {rankChange > 0 && "+"}
        {rankChange}
      </td>
      <td>
        {nFormatter(BigInt(item.points), 2)}
        <span className="fs-16 color-grey fw-400 block">{`${item.address.slice(0, 8)} ... ${item.address.slice(-12)}`}</span>
      </td>
      <td>
        <div className="leaderboard__transactions">
          <span
            className={clsx({
              "flex ai-c fw-500": true,
              "color-teal": item.badges?.length,
            })}>
            {item.totalTx}
            {item.badges?.map((b) => (
              <img
                key={b}
                src={bolt}
                alt="bolt"
                className="w-2.5 h-a ml-0.5"
                data-tooltip-id="global-tip"
                data-tooltip-content={t("leaderTooltip", { badge: b })}
              />
            ))}
          </span>
          {item.rank === 1 && (
            <img src={trophy} alt="trophy" className="w-a h-5" />
          )}
          {item.rank === 2 && (
            <img src={silver} alt="medal" className="w-a h-5" />
          )}
          {item.rank === 3 && (
            <img src={bronze} alt="medal" className="w-a h-5" />
          )}
        </div>
      </td>
    </tr>
  );
};

const Item: FC<{
  k: LeaderboardItemFragment$key;
}> = ({ k }) => {
  const item = useFragment(itemFragment, k);
  const rankChange = item.rankPrevious ? item.rankPrevious - item.rank : 0;
  return (
    <tr
      className={clsx({
        "fs-16 fw-400": true,
        gold: item.rank === 1,
        silver: item.rank === 2,
        bronze: item.rank === 3,
      })}>
      <td className="text-center">{item.rank}</td>
      <td
        className={clsx({
          "text-center": true,
          "color-teal": rankChange > 0,
          "color-red": rankChange < 0,
          "color-grey": rankChange === 0,
        })}>
        {rankChange > 0 && <Icons.TrendUp className="w-1.5" />}
        {rankChange < 0 && <Icons.TrendDown className="w-1.5" />}{" "}
        {rankChange > 0 && "+"}
        {rankChange}
      </td>
      <td>
        {nFormatter(BigInt(item.points), 2, 8)}
        <span className="fs-14 color-grey fw-400 block">{`${item.address.slice(0, 8)} ... ${item.address.slice(-12)}`}</span>
      </td>
      <td>
        <div className="leaderboard__transactions">
          {item.badges?.length ? (
            <span className="flex ai-c fw-500 color-teal">
              {item.totalTx.toLocaleString()}
              <img src={bolt} alt="bolt" className="w-2.5 h-a ml-0.5" />
            </span>
          ) : (
            <span>{item.totalTx.toLocaleString()}</span>
          )}
          {item.rank === 1 && (
            <img src={trophy} alt="trophy" className="w-a h-3" />
          )}
          {item.rank === 2 && (
            <img src={silver} alt="medal" className="w-a h-3" />
          )}
          {item.rank === 3 && (
            <img src={bronze} alt="medal" className="w-a h-3" />
          )}
        </div>
      </td>
    </tr>
  );
};

const NoResults: FC<{ count: number }> = ({ count }) => {
  const { t } = useTranslation("leagues");
  if (count > 0) return null;
  return (
    <tr>
      <td
        colSpan={LEADERBOARD_COLUMNS.length}
        className="text-center color-white fs-16 fw-400 py-3">
        {t("noResultsFound")}
      </td>
    </tr>
  );
};

const LEADERBOARD_LOADING_ROWS = [...Array(50)];

const LeaderboardFallback: FC = () => {
  const { pageRows } = useTablePagination(LEADERBOARD_LOADING_ROWS);

  return (
    <TableFrame
      className="leaderboard__table mt-2"
      columns={LEADERBOARD_COLUMNS}
      tableClassName="table table--fixed condensed mb-0">
      <Header />
      <tbody>
        {pageRows.map((_, i) => (
          <tr key={i} className={`opacity-${20 - Math.min(i, 9) * 2}`}>
            {LEADERBOARD_COLUMNS.map(({ id }) => (
              <td key={id}>
                <div className="skeleton br-2 h-1.5" />
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </TableFrame>
  );
};

const Header: FC = () => {
  const { t } = useTranslation("leagues");
  const [{ sortBy, sortDir }, { setSortBy, setSortDir }] = useContext(context);

  const onSort = (value: LeagueLeaderboardSortBy) => {
    if (sortBy === value) {
      setSortDir(sortDir === "ASC" ? "DESC" : "ASC");
      return;
    }

    setSortBy(value);
  };

  const sortHeaderProps = {
    active: sortBy,
    dir: sortDir === "ASC" ? TableSortDir.Asc : TableSortDir.Desc,
    onSort,
  };

  return (
    <thead>
      <tr>
        <SortableTableHeader
          {...sortHeaderProps}
          label={t("rank")}
          value="RANK"
          align="center"
        />
        <th scope="col" className="text-center">
          {t("change7d")}
        </th>
        <SortableTableHeader
          {...sortHeaderProps}
          label={t("points")}
          value="POINTS"
        />
        <SortableTableHeader
          {...sortHeaderProps}
          label={t("totalTxs")}
          value="TOTAL_TX"
        />
      </tr>
    </thead>
  );
};

const AccountProvider: FC<
  PropsWithChildren<{
    account: Address;
    Component: FC<
      PropsWithChildren<{
        a: PreloadedQuery<LeaderboardAccountQuery>;
        address: Address;
      }>
    >;
  }>
> = ({ account, children, Component }) => {
  const [a, loadAccountQuery] =
    useQueryLoader<LeaderboardAccountQuery>(accountQuery);

  useEffect(() => {
    loadAccountQuery({
      id: Buffer.from(`LeagueAccount:genesis/0/${account.address}`).toString(
        "base64"
      ),
    });
  }, [account]);

  return a ? (
    <Component a={a} address={account}>
      {children}
    </Component>
  ) : null;
};

const Position: FC<{
  address: Address;
  a: PreloadedQuery<LeaderboardAccountQuery>;
}> = ({ address, a }) => {
  const { t } = useTranslation("leagues");
  const { selected } = useWalletAccounts();
  const account = usePreloadedQuery(accountQuery, a);
  const rankChange =
    account.node?.rankPrevious && account.node.rank
      ? account.node?.rankPrevious - account.node?.rank
      : 0;

  const ref = useRef<HTMLDivElement>(null);
  const { copy, share } = useCapture(ref);

  /* const Prepare = (then: () => void) => {
    then();
  } */

  return (
    <>
      {/* Start Screenshot Capture Element */}
      <div className="leaderboard__share" ref={ref}>
        <div className="fs-16 lh-18 fw-400 color-grey">{t("rujiLeagues")}</div>
        <div className="fs-22 lh-22 fw-400 mt-0.5">
          {t("leaderboardPosition")}
        </div>
        <div className="flex ai-c mt-2">
          {account.node?.rank === 1 && (
            <img src={trophy} alt="trophy" className="w-a h-3 mr-1.5" />
          )}
          {account.node?.rank === 2 && (
            <img src={silver} alt="trophy" className="w-a h-3 mr-1.5" />
          )}
          {account.node?.rank === 3 && (
            <img src={bronze} alt="trophy" className="w-a h-3 mr-1.5" />
          )}
          <div className="fs-28 condensed fw-500 mr-2.5">
            #{account.node?.rank || "N/A"}
          </div>
          <div
            className={clsx({
              "fs-28 condensed fw-500 mr-1.5": true,
              "color-teal": rankChange > 0,
              "color-red": rankChange < 0,
              "color-grey": rankChange === 0,
            })}>
            {rankChange > 0 && <Icons.TrendUp className="w-2" />}
            {rankChange < 0 && <Icons.TrendDown className="w-2" />}{" "}
            {rankChange > 0 && "+"}
            {rankChange}
          </div>
        </div>
        <div className="flex ai-s mt-2 fs-22 fs-md-32 condensed fw-500">
          <div className="">
            {nFormatter(BigInt(account.node?.points || 0), 2)}{" "}
            <small className="fs-18 fs-md-22">
              {t("points").toLowerCase()}
            </small>
            <span className="fs-14 color-grey fw-400 block mt-0.5">
              https://rujira.network
            </span>
          </div>
        </div>
        <img
          src={rujiraLogo}
          alt="Rujira"
          className="leaderboard__share-logo mt-a ml-a"
        />
      </div>
      {/* End Screenshot Capture Element */}
      <div>
        <div
          className={clsx({
            "card card--semi-transparent card--blur h-full p-3": true,
            leaderboard__wallet: selected,
          })}>
          <div className="flex wrap-ai-c">
            <h2 className="fs-16 lh-22 fw-400 color-grey">{t("myPosition")}</h2>
            <Button
              className="button--xs ml-a"
              onClick={share}
              label={t("share")}
            />
            <Button
              className="button--xs ml-0.5"
              onClick={copy}
              label={t("screenshot")}
            />
          </div>
          {selected ? (
            <div className="flex ai-c">
              {account.node && (
                <>
                  <div className="fs-18 fs-md-24 condensed fw-500 mr-2.5">
                    #{account.node?.rank}
                  </div>
                  <div
                    className={clsx({
                      "fs-18 fs-md-24 condensed fw-500 mr-2.5": true,
                      "color-teal": rankChange > 0,
                      "color-red": rankChange < 0,
                      "color-grey": rankChange === 0,
                    })}>
                    {rankChange > 0 && <Icons.TrendUp className="w-2" />}
                    {rankChange < 0 && <Icons.TrendDown className="w-2" />}{" "}
                    {rankChange > 0 && "+"}
                    {rankChange}
                  </div>
                </>
              )}
              <div className="fs-22 fs-md-32 condensed fw-500">
                {nFormatter(BigInt(account.node?.points || 0), 2)}{" "}
                <small className="fs-18 fs-md-22">
                  {t("points").toLowerCase()}
                </small>
                <span className="fs-14 color-grey fw-400 block">
                  {`${address?.address.slice(0, 8)} ... ${address?.address.slice(-12)}`}
                </span>
              </div>
            </div>
          ) : (
            <p className="mt-1">{t("connectWalletMessage")}</p>
          )}
        </div>
      </div>
    </>
  );
};
